import type { ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';

import { FormFieldDropdown } from '~/components/FormField';
import { useUserTypes } from '~settings/api/queries/userTypes';
import { UserTypeMap } from '~settings/constants/UserTypeMap';

interface SelectUserTypeProps
  extends Omit<ComponentProps<typeof FormFieldDropdown>, 'label' | 'options'>,
    Partial<Pick<ComponentProps<typeof FormFieldDropdown>, 'label'>> {}

const mapRoles = (
  roles: readonly { id: string; name: string }[],
  t: (key: string) => string,
) =>
  roles
    .filter(({ name }) => Object.keys(UserTypeMap).includes(name))
    .map((role) => ({
      value: role.name,
      label: t(`${UserTypeMap[role.name as keyof typeof UserTypeMap]}.name`),
    }));

export function SelectUserType({ name, ...props }: SelectUserTypeProps) {
  const { t } = useTranslation('settings', { keyPrefix: 'dropdowns.userType' });
  const { t: rolesT } = useTranslation('settings', { keyPrefix: 'userTypes' });

  const { userTypes, isLoading } = useUserTypes();

  return (
    <FormFieldDropdown
      label={t('title', { count: 1 })}
      placeholder={t('placeholder', { count: 1 })}
      {...props}
      loading={isLoading}
      name={name}
      options={mapRoles(userTypes?.data || [], rolesT)}
      readOnly={isLoading || props.readOnly}
    />
  );
}

SelectUserType.displayName = 'SelectUserType';
